import { computed, inject } from '@angular/core';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { I18nService } from '@lib/i18n';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { DemandSelectableCurrencies } from '../../types/IDemandSelectableCurrencies';

/**
 * EPROC-11560: state of the check whether the suppliers of the demand accept its document currency.
 * EPROC-11425/11560 Konsolidierung: fed by `GET /demand/{id}/selectable-currencies` — the one
 * endpoint that also serves the currency banner and the supplier filter.
 */
export enum SupplierCurrencyCheckStatus {
    /** A check is running and has not answered yet — the order release must stay blocked (fail closed). */
    PENDING = 'PENDING',
    /** The suppliers accept the document currency, or the rule does not apply to this demand. */
    COMPATIBLE = 'COMPATIBLE',
    /** The suppliers do not accept the document currency — the order release is blocked with a reason. */
    CONFLICT = 'CONFLICT',
    /** The check failed technically — the order release stays blocked until a retry answered (fail closed). */
    ERROR = 'ERROR',
}

interface ISupplierCurrencyState {
    readonly status: SupplierCurrencyCheckStatus;
    readonly documentCurrency: string | null;
    readonly acceptedCurrencies: string[];
    readonly supplierName: string | null;
}

const initialState: ISupplierCurrencyState = {
    status: SupplierCurrencyCheckStatus.PENDING,
    documentCurrency: null,
    acceptedCurrencies: [],
    supplierName: null,
};

const CONFLICT_MESSAGE = marker(
    'Die Belegwährung {{documentCurrency}} wird von {{supplier}} nicht akzeptiert. Die Freigabe ist erst möglich, wenn Währung und Lieferant zusammenpassen.',
);
const ACCEPTED_CURRENCIES_ADDENDUM = marker('Akzeptierte Währungen: {{acceptedCurrencies}}.');
const CHECK_FAILED = marker(
    'Die Währungskompatibilität mit dem Lieferanten konnte nicht geprüft werden. Die Freigabe bleibt gesperrt, bis die Prüfung erfolgreich war. Bitte versuchen Sie es erneut.',
);

/**
 * Holds the supplier currency compatibility state of one demand (EPROC-11560). It decides whether
 * the ORDER release may happen: only an answered, compatible check unblocks it, so pending checks,
 * conflicts and technical failures all keep it blocked (fail closed). EPROC-11425/11560
 * Konsolidierung: harter Block nur Bestellweg — announcing the demand and converting it to an RFQ
 * are scoped out by the component (`DemandContentComponent.applySupplierCurrencyState`), not here.
 */
export const SupplierCurrencyStore = signalStore(
    withState(initialState),
    withComputed((state, i18n = inject(I18nService)) => ({
        /**
         * Only an answered, compatible check allows the order release. A technical failure leaves the
         * compatibility unknown, so it must not unblock the release either — otherwise a failing
         * request would re-enable the order release for an incompatible currency.
         */
        releaseAllowed: computed(() => state.status() === SupplierCurrencyCheckStatus.COMPATIBLE),
        /** Ready-to-render reason shown at the supplier, `null` while there is no conflict. */
        conflictMessage: computed<string | null>(() => {
            if (state.status() !== SupplierCurrencyCheckStatus.CONFLICT) {
                return null;
            }
            let message = i18n.translateInstant(CONFLICT_MESSAGE, {
                documentCurrency: state.documentCurrency() ?? '',
                supplier: state.supplierName() ?? '',
            });
            const acceptedCurrencies = state.acceptedCurrencies();
            if (acceptedCurrencies.length > 0) {
                message +=
                    ' ' +
                    i18n.translateInstant(ACCEPTED_CURRENCIES_ADDENDUM, {
                        acceptedCurrencies: acceptedCurrencies.join(', '),
                    });
            }
            return message;
        }),
        /** Readable technical error shown together with a retry action. */
        checkErrorMessage: computed<string | null>(() =>
            state.status() === SupplierCurrencyCheckStatus.ERROR ? i18n.translateInstant(CHECK_FAILED) : null,
        ),
    })),
    withMethods((store) => ({
        /** A new check started: block the order release until it answered. */
        markPending(): void {
            patchState(store, { status: SupplierCurrencyCheckStatus.PENDING });
        },

        /** A technical failure keeps the order release blocked and shows the retry indication. */
        markFailed(): void {
            patchState(store, { status: SupplierCurrencyCheckStatus.ERROR });
        },

        /**
         * Takes over the verdict of the selectable-currencies lookup together with the supplier it
         * was answered for (EPROC-11425/11560 Konsolidierung): `accepted` decides compatibility, the
         * selectable currencies are the ones customer and suppliers share.
         */
        applyResult(selectable: DemandSelectableCurrencies, supplierName: string | null): void {
            patchState(store, {
                status: selectable.accepted
                    ? SupplierCurrencyCheckStatus.COMPATIBLE
                    : SupplierCurrencyCheckStatus.CONFLICT,
                documentCurrency: selectable.demandCurrency ?? null,
                acceptedCurrencies: selectable.currencies,
                supplierName,
            });
        },
    })),
);
